import type { Ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  ListDeliveryAvailabilityDocument,
  ListDeliveryAvailabilityQuery,
} from "@/generated/graphql";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  validateStartTimeAfterNow,
  validateStartTimeAfterDeliveryStart,
  validateStartTimeBeforeDeliveryEnd,
} from "@/common/deliveryTimeValidationUtil";
import type { DeliveryAvailabilityData, DeliveryArea } from "../types";

/**
 * 配達希望日時の選択制御に必要な依存をまとめた型。
 */
interface UseDeliveryRequestRegisterDeliveryTimeParams {
  listDeliveryArea: Ref<DeliveryArea[] | null>;
  deliveryAvailabilityStore: Ref<DeliveryAvailabilityData[][]>;
  deliveryAvailabilityHeader: Ref<Map<string, string>>;
  deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  disp_deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  deliveryRequestCreateInput: any;
  deliveryAreaName: Ref<string>;
  deliveryId_0_Flg: Ref<boolean>;
  deliveryStartHourItem: Ref<number[]>;
  deliveryStartHourValues: Ref<number | undefined>;
  deliveryStartMinitItem: Ref<{ label: string; deliveryStartMinit: number }[]>;
  deliveryStartMinitValues: Ref<{ label: string; deliveryStartMinit: number } | undefined>;
  displayDeliveryTime: Ref<string>;
  deliverySelectDayBox: Ref<string>;
  deliverySelectBox: Ref<string>;
  deliveryStartTime: Ref<string>;
  deliveryEndTime: Ref<string>;
  acceptableEndTime: Ref<string>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  sectionBlockList: Ref<any>;
  blockList: Ref<any>;
  originalCityAddress: Ref<string>;
  originalSectionAddress: Ref<string>;
  originalAddress: Ref<string>;
  originalBlockName: Ref<string>;
  zipCodeFromPref: Ref<string>;
  irregularDeliveryStartTime: Ref<string>;
  irregularDeliveryEndTime: Ref<string>;
  irregularDeliveryDisplayTime: Ref<string>;
}

/**
 * 配達便一覧の生成、都度便の時刻入力、選択結果の反映を扱う composable。
 */
export const useDeliveryRequestRegisterDeliveryTime = ({
  listDeliveryArea,
  deliveryAvailabilityStore,
  deliveryAvailabilityHeader,
  deliveryAvailabilityData,
  disp_deliveryAvailabilityData,
  deliveryRequestCreateInput,
  deliveryAreaName,
  deliveryId_0_Flg,
  deliveryStartHourItem,
  deliveryStartHourValues,
  deliveryStartMinitItem,
  deliveryStartMinitValues,
  displayDeliveryTime,
  deliverySelectDayBox,
  deliverySelectBox,
  deliveryStartTime,
  deliveryEndTime,
  acceptableEndTime,
  errdialog,
  errMessage,
  sectionBlockList,
  blockList,
  originalCityAddress,
  originalSectionAddress,
  originalAddress,
  originalBlockName,
  zipCodeFromPref,
  irregularDeliveryStartTime,
  irregularDeliveryEndTime,
  irregularDeliveryDisplayTime,
}: UseDeliveryRequestRegisterDeliveryTimeParams) => {
  /**
   * 目的: 住所に紐づく配達可能枠を取得・整形し、画面表示用データへ反映する。
   * 利用方法: 住所選択や初期表示時に呼び出し、配達便一覧を構築する。
   */
  const getDeliveryTimeList = async (): Promise<void> => {
    try {
      const apolloClient = await generateApolloClient();

      let result: ApolloQueryResult<ListDeliveryAvailabilityQuery>;
      let list: any[] = [];

      if (deliveryAvailabilityStore.value.length > 0) {
        for (let i = 0; i < listDeliveryArea.value!.length; i++) {
          for (let j = 0; j < deliveryAvailabilityStore.value[0].length; j++) {
            if (
              deliveryAvailabilityStore.value[0][j] !== undefined &&
              deliveryAvailabilityStore.value[0][j] !== null &&
              listDeliveryArea.value![i] !== undefined &&
              listDeliveryArea.value![i] !== null &&
              listDeliveryArea.value![i]!.area_code ==
                deliveryAvailabilityStore.value[0][j].area_code
            ) {
              list.push(deliveryAvailabilityStore.value[0][j]);
            }
          }

          if (list === undefined || list === null || list.length <= 0) {
            errdialog.value = true;
            errMessage.value = "選択した住所で配達可能な時間帯はありません。";
            deliveryRequestCreateInput.area_code = "";
            deliveryAreaName.value = "";
            deliveryAvailabilityData.value = [];
            disp_deliveryAvailabilityData.value = [];
            sectionBlockList.value = null;
            blockList.value = [];
            originalCityAddress.value = "";
            originalSectionAddress.value = "";
            deliveryRequestCreateInput.delivery_address1 =
              originalAddress.value;
            return;
          }

          let defaultAreaCode = "";
          let currentDate = "";
          let currentAvialibity: DeliveryAvailabilityData[] = [];
          let idx = 0;
          let addDeliveryIdAry: number[] = [];
          let addCnt = 0;
          let current_area_code = "";

          addDeliveryIdAry = list.map(function (item) {
            return item["delivery_id"];
          });
          addDeliveryIdAry = Array.from(new Set(addDeliveryIdAry));

          list.forEach((value) => {
            if (value !== undefined && value !== null) {
              if (defaultAreaCode.length == 0) {
                defaultAreaCode = value.area_code;
              }
              if (value.area_code !== defaultAreaCode) {
                return;
              }
              current_area_code = listDeliveryArea.value![i]!.area_code;
              value.delivery_date = value.delivery_date.substring(0, 10);
              if (currentDate.length == 0 || currentDate != value.delivery_date) {
                currentDate = value.delivery_date;
                if (currentAvialibity.length > 0) {
                  if (addCnt < addDeliveryIdAry.length) {
                    for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
                      currentAvialibity.push({
                        key: ++idx,
                        area_code: current_area_code,
                        delivery_id: addDeliveryIdAry[j],
                        delivery_id_desc: "",
                        delivery_date: value.delivery_date,
                        delivery_start_time: "",
                        delivery_end_time: "",
                        availability: "×",
                        already_requested: 0,
                      });
                    }
                  }
                  deliveryAvailabilityData.value.push(currentAvialibity);
                  if (current_area_code == deliveryRequestCreateInput.area_code) {
                    disp_deliveryAvailabilityData.value.push(currentAvialibity);
                  }
                }
                currentAvialibity = [];
                idx = 0;
                addCnt = 0;
              }

              for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
                if (addDeliveryIdAry[j] !== value.delivery_id) {
                  currentAvialibity.push({
                    key: ++idx,
                    area_code: current_area_code,
                    delivery_id: addDeliveryIdAry[j],
                    delivery_id_desc: "",
                    delivery_date: value.delivery_date,
                    delivery_start_time: "",
                    delivery_end_time: "",
                    availability: "×",
                    already_requested: 0,
                  });
                } else {
                  addCnt = j;
                  break;
                }
              }

              currentAvialibity.push({
                key: ++idx,
                area_code: current_area_code,
                delivery_id: value.delivery_id,
                delivery_id_desc: value.delivery_id_desc,
                delivery_date: value.delivery_date,
                delivery_start_time: value.delivery_start_time,
                delivery_end_time: value.delivery_end_time,
                availability: value.availability.trim(),
                already_requested: value.already_requested
                  ? value.already_requested
                  : 0,
              });
              addCnt++;
            }
          });

          if (currentAvialibity.length > 0) {
            if (addCnt < addDeliveryIdAry.length) {
              for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
                currentAvialibity.push({
                  key: ++idx,
                  area_code: current_area_code,
                  delivery_id: addDeliveryIdAry[j],
                  delivery_id_desc: "",
                  delivery_date: currentDate,
                  delivery_start_time: "",
                  delivery_end_time: "",
                  availability: "×",
                  already_requested: 0,
                });
              }
            }
            deliveryAvailabilityData.value.push(currentAvialibity);
            if (current_area_code == deliveryRequestCreateInput.area_code) {
              disp_deliveryAvailabilityData.value.push(currentAvialibity);
            }
          }

          if (current_area_code == deliveryRequestCreateInput.area_code) {
            deliveryRequestCreateInput.delivery_address1 =
              originalAddress.value + originalBlockName.value;
            if (zipCodeFromPref.value.length > 0) {
              deliveryRequestCreateInput.delivery_zip_code =
                zipCodeFromPref.value;
            }
          }

          list = [];
        }

        if (listDeliveryArea.value!.length == 1) {
          if (deliveryAvailabilityData.value[0][0].delivery_id == 0) {
            deliveryId_0_Flg.value = true;
            deliveryStartTime.value =
              deliveryAvailabilityData.value[0][0].delivery_start_time;
          }
        }
      } else {
        for (let i = 0; i < listDeliveryArea.value!.length; i++) {
          result = await apolloClient.query<ListDeliveryAvailabilityQuery>({
            query: ListDeliveryAvailabilityDocument,
            variables: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: localStorageGetShopCode(),
              area_code: listDeliveryArea.value![i]!.area_code,
            },
            fetchPolicy: "no-cache",
          });

          list = result.data.listDeliveryAvailability!;
          if (list === undefined || list === null || list.length <= 0) {
            errdialog.value = true;
            errMessage.value = "選択した住所で配達可能な時間帯はありません。";
            deliveryRequestCreateInput.area_code = "";
            deliveryAreaName.value = "";
            deliveryAvailabilityData.value = [];
            disp_deliveryAvailabilityData.value = [];
            sectionBlockList.value = null;
            blockList.value = [];
            originalCityAddress.value = "";
            originalSectionAddress.value = "";
            deliveryRequestCreateInput.delivery_address1 =
              originalAddress.value;
            return;
          }

          let defaultAreaCode = "";
          let currentDate = "";
          let currentAvialibity: DeliveryAvailabilityData[] = [];
          let idx = 0;
          let addDeliveryIdAry: number[] = [];
          let addCnt = 0;
          let current_area_code = "";

          addDeliveryIdAry = list.map(function (item) {
            return item!["delivery_id"];
          });
          addDeliveryIdAry = Array.from(new Set(addDeliveryIdAry));

          list.forEach((value) => {
            if (value !== undefined && value !== null) {
              if (defaultAreaCode.length == 0) {
                defaultAreaCode = value.area_code;
              }
              if (value.area_code !== defaultAreaCode) {
                return;
              }
              current_area_code = listDeliveryArea.value![i]!.area_code;
              value.delivery_date = value.delivery_date.substring(0, 10);
              if (currentDate.length == 0 || currentDate != value.delivery_date) {
                currentDate = value.delivery_date;
                if (currentAvialibity.length > 0) {
                  if (addCnt < addDeliveryIdAry.length) {
                    for (let k = addCnt; k < addDeliveryIdAry.length; k++) {
                      currentAvialibity.push({
                        key: ++idx,
                        area_code: current_area_code,
                        delivery_id: addDeliveryIdAry[k],
                        delivery_id_desc: "",
                        delivery_date: value.delivery_date,
                        delivery_start_time: "",
                        delivery_end_time: "",
                        availability: "×",
                        already_requested: 0,
                      });
                    }
                  }
                  deliveryAvailabilityData.value.push(currentAvialibity);
                  if (current_area_code == deliveryRequestCreateInput.area_code) {
                    disp_deliveryAvailabilityData.value.push(currentAvialibity);
                  }
                }
                currentAvialibity = [];
                idx = 0;
                addCnt = 0;
              }

              for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
                if (addDeliveryIdAry[j] !== value.delivery_id) {
                  currentAvialibity.push({
                    key: ++idx,
                    area_code: current_area_code,
                    delivery_id: addDeliveryIdAry[j],
                    delivery_id_desc: "",
                    delivery_date: value.delivery_date,
                    delivery_start_time: "",
                    delivery_end_time: "",
                    availability: "×",
                    already_requested: 0,
                  });
                } else {
                  addCnt = j;
                  break;
                }
              }

              currentAvialibity.push({
                key: ++idx,
                area_code: current_area_code,
                delivery_id: value.delivery_id,
                delivery_id_desc: value.delivery_id_desc,
                delivery_date: value.delivery_date,
                delivery_start_time: value.delivery_start_time,
                delivery_end_time: value.delivery_end_time,
                availability: value.availability.trim(),
                already_requested: value.already_requested
                  ? value.already_requested
                  : 0,
              });
              addCnt++;
            }
          });

          if (currentAvialibity.length > 0) {
            if (addCnt < addDeliveryIdAry.length) {
              for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
                currentAvialibity.push({
                  key: ++idx,
                  area_code: current_area_code,
                  delivery_id: addDeliveryIdAry[j],
                  delivery_id_desc: "",
                  delivery_date: currentDate,
                  delivery_start_time: "",
                  delivery_end_time: "",
                  availability: "×",
                  already_requested: 0,
                });
              }
            }
            deliveryAvailabilityData.value.push(currentAvialibity);
            if (current_area_code == deliveryRequestCreateInput.area_code) {
              disp_deliveryAvailabilityData.value.push(currentAvialibity);
            }
          }

          if (current_area_code == deliveryRequestCreateInput.area_code) {
            deliveryRequestCreateInput.delivery_address1 =
              originalAddress.value + originalBlockName.value;
            if (zipCodeFromPref.value.length > 0) {
              deliveryRequestCreateInput.delivery_zip_code =
                zipCodeFromPref.value;
            }
          }

          list = [];
        }

        if (listDeliveryArea.value!.length == 1) {
          if (deliveryAvailabilityData.value[0][0].delivery_id == 0) {
            deliveryId_0_Flg.value = true;
            deliveryStartTime.value =
              deliveryAvailabilityData.value[0][0].delivery_start_time;
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "選択した住所は配達エリア外です。";
        throw err;
      } else {
        throw new Error("選択した住所は配達エリア外です。（Unknown error）");
      }
    }
  };

  /**
   * 目的: ユーザーが選択した配達便/日付を申込データへ反映し、都度便(0便)選択時の時刻入力候補を初期化する。
   * 利用方法: 配達枠セルのクリックイベントから呼び出す。
   */
  const onDeliveryAvailabilityClick = async (
    date: string,
    deliveryid: string,
    delivery_start_time: string,
    delivery_end_time: string,
    deliverySelectDay: string,
    deliverySelect: string
  ): Promise<void> => {
    deliveryRequestCreateInput.delivery_id = Number(deliveryid);
    deliveryRequestCreateInput.required_delivery_date = date;

    let strDeliveryId = "";
    deliveryId_0_Flg.value = false;
    deliveryStartHourItem.value = [];
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitValues.value = undefined;
    if (deliveryRequestCreateInput.delivery_id === 0) {
      let startHour = 0;
      const now = new Date();
      const nowDate =
        now.getFullYear() +
        "-" +
        String(now.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(now.getDate()).padStart(2, "0");
      const deliveryStartTimeAry = delivery_start_time.split(":");

      if (
        date !== nowDate ||
        (date == nowDate &&
          (Number(deliveryStartTimeAry[0]) > now.getHours() ||
            (Number(deliveryStartTimeAry[0]) == now.getHours() &&
              Number(deliveryStartTimeAry[1]) > now.getMinutes())))
      ) {
        startHour = Number(deliveryStartTimeAry[0]);
      } else {
        if (now.getMinutes() < 30) {
          startHour = now.getHours();
        } else {
          startHour = now.getHours() + 1;
        }
      }

      const endTimeAry = acceptableEndTime.value.split(":");
      for (let i = startHour; i <= Number(endTimeAry[0]); i++) {
        deliveryStartHourItem.value.push(i);
      }

      deliveryStartMinitItem.value = [
        { label: "00", deliveryStartMinit: 0 },
        { label: "30", deliveryStartMinit: 1 },
      ];

      strDeliveryId = "都度便";
      deliveryId_0_Flg.value = true;
      deliveryStartTime.value = delivery_start_time;
      deliveryEndTime.value = delivery_end_time;

      await setDeliveryStartTime();
      await setDeliveryEndTime();

      deliveryRequestCreateInput.irregular_delivery_start_time = null;
      deliveryRequestCreateInput.irregular_delivery_end_time = null;
    } else {
      strDeliveryId = deliveryRequestCreateInput.delivery_id + "便";
    }

    displayDeliveryTime.value =
      deliveryRequestCreateInput.required_delivery_date + "  " + strDeliveryId;
    deliverySelectDayBox.value = deliverySelectDay;
    deliverySelectBox.value = deliverySelect;
  };

  /**
   * 目的: 当日配送時に、入力した希望時刻が現在時刻より後かを判定する。
   * 利用方法: 時刻(時・分)変更時のバリデーション内で呼び出す。
   */
  const chkDeliveryStartTime_Now = async (): Promise<boolean> => {
    return validateStartTimeAfterNow(
      deliveryRequestCreateInput.required_delivery_date,
      deliveryStartHourValues.value,
      deliveryStartMinitValues.value?.label
    );
  };

  const chkDeliveryStartTime = async (): Promise<boolean> => {
    return validateStartTimeAfterDeliveryStart(
      deliveryStartTime.value,
      deliveryStartHourValues.value,
      deliveryStartMinitValues.value?.label
    );
  };

  /**
   * 目的: 入力した希望時刻が受付終了時刻を超過していないかを判定する。
   * 利用方法: 時刻(時・分)変更時のバリデーション内で呼び出す。
   */
  const chkDeliveryEndTimeOver = async (): Promise<boolean> => {
    return validateStartTimeBeforeDeliveryEnd(
      deliveryEndTime.value,
      deliveryStartHourValues.value,
      deliveryStartMinitValues.value?.label
    );
  };

  /**
   * 目的: 都度便の開始時刻表示(`irregularDeliveryStartTime`)を確定する。
   * 利用方法: 便選択時、または時/分の変更後に呼び出し、表示文言も同時に更新する。
   */
  const setDeliveryStartTime = async (): Promise<void> => {
    const now = new Date();
    const deliveryStartTimeAry = deliveryStartTime.value.split(":");
    if (
      deliveryStartHourValues.value !== undefined &&
      deliveryStartHourValues.value !== null &&
      (deliveryStartMinitValues.value === undefined ||
        deliveryStartMinitValues.value === null)
    ) {
      // 時のみ選択中は未確定扱い
      irregularDeliveryStartTime.value = "";
      irregularDeliveryDisplayTime.value = "";
      return;
    }
    if (
      deliveryStartHourValues.value !== undefined &&
      deliveryStartHourValues.value !== null &&
      deliveryStartMinitValues.value !== undefined &&
      deliveryStartMinitValues.value !== null
    ) {
      irregularDeliveryStartTime.value =
        String(deliveryStartHourValues.value).padStart(2, "0") +
        ":" +
        deliveryStartMinitValues.value.label;
      irregularDeliveryDisplayTime.value =
        irregularDeliveryStartTime.value + "から3時間以内";
    } else {
      let hour = 0;
      let minut = 0;
      if (
        now.getHours() < Number(deliveryStartTimeAry[0]) ||
        (now.getHours() == Number(deliveryStartTimeAry[0]) &&
          now.getMinutes() < Number(deliveryStartTimeAry[1]))
      ) {
        hour = Number(deliveryStartTimeAry[0]);
        minut = Number(deliveryStartTimeAry[1]);
      } else {
        hour = now.getHours();
        minut = now.getMinutes();
      }

      irregularDeliveryStartTime.value =
        String(hour).padStart(2, "0") + ":" + String(minut).padStart(2, "0");
      irregularDeliveryDisplayTime.value =
        `登録完了画面でご確認ください（配達開始時間は${deliveryStartTime.value}です）`;
    }
  };

  /**
   * 目的: 都度便の終了時刻(`irregularDeliveryEndTime`)を「開始から3時間以内」かつ受付終了時刻内で確定する。
   * 利用方法: `setDeliveryStartTime` の後に呼び出し、開始時刻に追従して終了時刻を更新する。
   */
  const setDeliveryEndTime = async (): Promise<void> => {
    if (irregularDeliveryStartTime.value.length === 0) {
      irregularDeliveryEndTime.value = "";
      return;
    }
    const deliveryStartTimeAry = irregularDeliveryStartTime.value.split(":");
    const deliveryEndTimeAry = deliveryEndTime.value.split(":");
    const endTime = Number(deliveryStartTimeAry[0]) + 3;
    if (
      endTime > Number(deliveryEndTimeAry[0]) ||
      (endTime == Number(deliveryEndTimeAry[0]) &&
        deliveryStartTimeAry[1] > deliveryEndTimeAry[1])
    ) {
      irregularDeliveryEndTime.value = deliveryEndTime.value;
    } else {
      irregularDeliveryEndTime.value =
        String(endTime).padStart(2, "0") + ":" + deliveryStartTimeAry[1];
    }
  };

  /**
   * 目的: 希望時刻の「時」変更時にバリデーションを実施し、選択可能な分候補と開始/終了時刻を再計算する。
   * 利用方法: 時刻「時」のプルダウン変更イベントから呼び出す。
   */
  const onChangeDeliveryStartHour = async (): Promise<void> => {
    if (deliveryStartHourValues.value !== undefined) {
      if (!(await chkDeliveryStartTime_Now())) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、現在以降の時間を入力してください。";
        return;
      }
      if (!(await chkDeliveryStartTime())) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、選択した便の配達開始時間以降の時間を入力してください。";
        return;
      }
      if (!(await chkDeliveryEndTimeOver())) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、受付終了時間以前の時間を入力してください。";
        return;
      }
    }

    const now = new Date();
    const deliveryStartTimeAry = deliveryStartTime.value.split(":");
    const endTimeAry = acceptableEndTime.value.split(":");
    deliveryStartMinitItem.value = [
      { label: "00", deliveryStartMinit: 0 },
      { label: "30", deliveryStartMinit: 1 },
    ];

    if (
      deliveryStartHourValues.value !== undefined &&
      deliveryStartHourValues.value !== null
    ) {
      if (
        (deliveryStartHourValues.value == now.getHours() &&
          now.getMinutes() < 30) ||
        (deliveryStartHourValues.value == Number(deliveryStartTimeAry[0]) &&
          deliveryStartTimeAry[1] == "30")
      ) {
        deliveryStartMinitItem.value.shift();
        deliveryStartMinitValues.value = { label: "30", deliveryStartMinit: 1 };
      } else if (
        deliveryStartHourValues.value == Number(endTimeAry[0]) &&
        endTimeAry[1] == "00"
      ) {
        deliveryStartMinitItem.value.pop();
        deliveryStartMinitValues.value = { label: "00", deliveryStartMinit: 0 };
      }
    }

    await setDeliveryStartTime();
    await setDeliveryEndTime();
  };

  /**
   * 目的: 希望時刻の「分」変更時にバリデーションを実施し、開始/終了時刻を再計算する。
   * 利用方法: 時刻「分」のプルダウン変更イベントから呼び出す。
   */
  const onChangeDeliveryStartMinit = async (): Promise<void> => {
    if (deliveryStartMinitValues.value !== undefined) {
      if (!(await chkDeliveryStartTime_Now())) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、現在以降の時間を入力してください。";
        return;
      }
      if (!(await chkDeliveryStartTime())) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、選択した便の配達開始時間以降の時間を入力してください。";
        return;
      }
      if (!(await chkDeliveryEndTimeOver())) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、受付終了時間以前の時間を入力してください。";
        return;
      }
    }

    await setDeliveryStartTime();
    await setDeliveryEndTime();
  };

  return {
    getDeliveryTimeList,
    onDeliveryAvailabilityClick,
    onChangeDeliveryStartHour,
    onChangeDeliveryStartMinit,
    setDeliveryStartTime,
    setDeliveryEndTime,
  };
};
